import {
  computeFilledCameraPreviewLayout,
  readCameraCaptureDeviceKind,
  readCameraCaptureViewportForModal,
  readHubMainContentWidth,
  type CameraCaptureDeviceKind,
  type CameraCaptureViewport,
  type ViewportOrientation,
} from "./cameraCaptureLayout";
import { resolveCameraCaptureModalLayout } from "./resolveCameraCaptureModalLayout";

export type CameraCaptureLayoutProbeMeasured = {
  cardWidth: number | null;
  cardHeight: number | null;
  previewWidth: number | null;
  previewHeight: number | null;
};

export type CameraCaptureLayoutProbeCheck = {
  id: string;
  ok: boolean;
  message: string;
};

export type CameraCaptureLayoutProbe = {
  probeAt: string;
  modalOpen: boolean;
  pageContentWidth: number;
  viewport: CameraCaptureViewport;
  deviceKind: CameraCaptureDeviceKind;
  previewAspect: number;
  expected: {
    cardWidth: number;
    cardHeight: number;
    previewWidth: number;
    previewHeight: number;
  };
  measured: CameraCaptureLayoutProbeMeasured | null;
  checks: CameraCaptureLayoutProbeCheck[];
  ok: boolean;
};

const MEASUREMENT_TOLERANCE_PX = 4;

export function readCameraCaptureLayoutMeasured(): CameraCaptureLayoutProbeMeasured | null {
  if (typeof document === "undefined") return null;
  const card = document.querySelector("[data-camera-capture-probe-card]");
  const preview = document.querySelector("[data-camera-capture-probe-preview]");
  if (card == null && preview == null) return null;
  return {
    cardWidth: card instanceof HTMLElement ? card.clientWidth : null,
    cardHeight: card instanceof HTMLElement ? card.clientHeight : null,
    previewWidth: preview instanceof HTMLElement ? preview.clientWidth : null,
    previewHeight: preview instanceof HTMLElement ? preview.clientHeight : null,
  };
}

export function buildCameraCaptureLayoutProbe(input: {
  modalOpen: boolean;
  previewAspect: number;
  touchCoarse: boolean;
  touchHoverNone: boolean;
  floorTablet: boolean;
  measured?: CameraCaptureLayoutProbeMeasured | null;
  /** Test harness override when `window` / `main` are unavailable. */
  pageContentWidthOverride?: number;
  viewportOverride?: CameraCaptureViewport;
  hasHint?: boolean;
}): CameraCaptureLayoutProbe {
  const viewport = input.viewportOverride ?? readCameraCaptureViewportForModal();
  const pageContentWidth = input.pageContentWidthOverride ?? readHubMainContentWidth();
  const wideTouchKiosk =
    (input.touchCoarse || input.touchHoverNone) && viewport.width > 1280;
  const deviceKind = readCameraCaptureDeviceKind({
    floorTablet: input.floorTablet,
    wideTouchKiosk,
    touchCoarse: input.touchCoarse,
    touchHoverNone: input.touchHoverNone,
  });
  const resolved = resolveCameraCaptureModalLayout({
    viewport,
    pageContentWidth,
    previewAspect: input.previewAspect,
    touchCoarse: input.touchCoarse,
    touchHoverNone: input.touchHoverNone,
    floorTablet: input.floorTablet,
    hasHint: input.hasHint === true,
  });
  const expectedLayout = resolved.filledLayout ?? computeFilledCameraPreviewLayout({
    pageContentWidth,
    viewportHeight: viewport.height,
    aspectRatio: input.previewAspect,
    deviceKind,
    hasHint: input.hasHint,
  });
  const measured = input.measured ?? readCameraCaptureLayoutMeasured();
  const checks: CameraCaptureLayoutProbeCheck[] = [];

  checks.push({
    id: "layout_inputs_ready",
    ok: input.modalOpen ? resolved.inputsReady : true,
    message: `inputsReady=${String(resolved.inputsReady)}`,
  });

  checks.push({
    id: "layout_not_stuck_at_minimum",
    ok: input.modalOpen ? !resolved.stuckAtMinimum : true,
    message: `stuckAtMinimum=${String(resolved.stuckAtMinimum)} preview ${String(resolved.previewWidth)}x${String(resolved.previewHeight)}`,
  });

  checks.push({
    id: "page_content_width_positive",
    ok: pageContentWidth >= 280,
    message: `main column width is ${String(pageContentWidth)}px`,
  });

  checks.push({
    id: "card_width_matches_page_column",
    ok: expectedLayout.cardWidth === pageContentWidth,
    message: `expected card width ${String(expectedLayout.cardWidth)}px vs main ${String(pageContentWidth)}px`,
  });

  checks.push({
    id: "preview_fills_card_body_width",
    ok:
      deviceKind !== "desktop_webcam"
      || expectedLayout.previewHeight / expectedLayout.previewWidth >= 1,
    message: `expected preview ${String(expectedLayout.previewWidth)}x${String(expectedLayout.previewHeight)}`,
  });

  const minPreviewHeight = Math.floor((viewport.height - 200) * 0.45);
  checks.push({
    id: "preview_height_budget",
    ok: expectedLayout.previewHeight >= minPreviewHeight,
    message: `expected preview height ${String(expectedLayout.previewHeight)}px >= ${String(minPreviewHeight)}px budget`,
  });

  checks.push({
    id: "desktop_preview_not_a_thin_strip",
    ok:
      deviceKind !== "desktop_webcam"
      || expectedLayout.previewHeight / expectedLayout.previewWidth >= 1,
    message: `expected preview ${String(expectedLayout.previewWidth)}x${String(expectedLayout.previewHeight)}`,
  });

  if (measured != null && input.modalOpen) {
    for (const [key, expectedValue] of [
      ["cardWidth", expectedLayout.cardWidth],
      ["cardHeight", expectedLayout.cardHeight],
      ["previewWidth", expectedLayout.previewWidth],
      ["previewHeight", expectedLayout.previewHeight],
    ] as const) {
      const measuredValue = measured[key];
      const ok =
        measuredValue != null
        && Math.abs(measuredValue - expectedValue) <= MEASUREMENT_TOLERANCE_PX;
      checks.push({
        id: `measured_${key}`,
        ok,
        message: `measured ${key} ${String(measuredValue)}px vs expected ${String(expectedValue)}px`,
      });
    }
    if (deviceKind === "desktop_webcam") {
      const ratio =
        measured.previewWidth != null
        && measured.previewWidth > 0
        && measured.previewHeight != null
          ? measured.previewHeight / measured.previewWidth
          : 0;
      checks.push({
        id: "measured_preview_not_a_thin_strip",
        ok: ratio >= 1,
        message: `measured preview aspect height/width ${String(ratio.toFixed(3))}`,
      });
    }
  }

  return {
    probeAt: new Date().toISOString(),
    modalOpen: input.modalOpen,
    pageContentWidth,
    viewport,
    deviceKind,
    previewAspect: input.previewAspect,
    expected: {
      cardWidth: expectedLayout.cardWidth,
      cardHeight: expectedLayout.cardHeight,
      previewWidth: expectedLayout.previewWidth,
      previewHeight: expectedLayout.previewHeight,
    },
    measured,
    checks,
    ok: checks.every((check) => check.ok),
  };
}

/** Pure expectation helper for unit tests and API probes (no DOM). */
export function computeCameraCaptureLayoutExpectation(input: {
  pageContentWidth: number;
  viewportWidth: number;
  viewportHeight: number;
  orientation: ViewportOrientation;
  aspectRatio: number;
  deviceKind: CameraCaptureDeviceKind;
  hasHint?: boolean;
}): CameraCaptureLayoutProbe["expected"] {
  const layout = computeFilledCameraPreviewLayout({
    pageContentWidth: input.pageContentWidth,
    viewportHeight: input.viewportHeight,
    aspectRatio: input.aspectRatio,
    deviceKind: input.deviceKind,
    hasHint: input.hasHint,
  });
  return {
    cardWidth: layout.cardWidth,
    cardHeight: layout.cardHeight,
    previewWidth: layout.previewWidth,
    previewHeight: layout.previewHeight,
  };
}
